import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PublicRacerModal from "@/components/PublicRacerModal";

function DiscBadge({ d }) {
  return d === "pod"
    ? <span className="disc-chip-pod">Pod</span>
    : <span className="disc-chip-speeder">Speeder</span>;
}

function DriversTable({ discipline, onRacerClick, seasonId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { 
    const url = seasonId 
      ? `/standings/drivers?discipline=${discipline}&season_id=${seasonId}`
      : `/standings/drivers?discipline=${discipline}`;
    api.get(url).then((r) => setRows(r.data)); 
  }, [discipline, seasonId]);
  const leader = rows[0]?.points || 0;
  return (
    <div className="rtrl-card carbon-bg overflow-hidden">
      <div className="grid grid-cols-12 px-4 py-3 small-label border-b border-surface-line">
        <div className="col-span-1">Pos</div>
        <div className="col-span-4">Racer</div>
        <div className="col-span-3">Syndicate</div>
        <div className="col-span-1 text-right">Wins</div>
        <div className="col-span-1 text-right">Podiums</div>
        <div className="col-span-2 text-right">Points</div>
      </div>
      {rows.length === 0 && <div className="p-8 text-center text-white/50">No results yet.</div>}
      {rows.map((row, i) => (
        <div key={row.racer_id} data-testid={`standings-driver-row-${i}`}
          className="grid grid-cols-12 items-center px-4 py-3 border-b border-surface-line/60 hover:bg-white/[0.01] transition-colors"
          style={{ borderLeft: `4px solid ${row.syndicate?.color || "#2A2A38"}`, background: i === 0 ? "linear-gradient(90deg, rgba(229,25,25,0.10), transparent 60%)" : "transparent" }}>
          <div className={`col-span-1 font-display text-3xl ${i === 0 ? "text-white" : "text-rtrl-silver"}`}>{i + 1}</div>
          <div className="col-span-4 cursor-pointer group" onClick={() => onRacerClick(row.racer_id)}>
            <div className="text-white font-body group-hover:text-rtrl-red transition-colors">{row.racer.display_name} <span className="text-white/40 font-mono-tech text-xs">#{row.racer.number ?? "-"}</span></div>
            <div className="small-label">{row.racer.platform || "—"} · {row.racer.region || "—"}</div>
          </div>
          <div className="col-span-3">
            {row.syndicate ? (
              <Link to={`/syndicates/${row.syndicate.id}`} className="flex items-center gap-2 hover:text-rtrl-red transition-colors group">
                {row.syndicate.logo_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${row.syndicate.logo_url}`} alt="" className="w-5 h-5 rounded-full object-contain bg-void/50 shrink-0" />
                ) : (
                  <span className="w-2 h-2 inline-block shrink-0" style={{ background: row.syndicate.color }} />
                )}
                <span className="text-white/80 group-hover:text-rtrl-red transition-colors">{row.syndicate.name}</span>
              </Link>
            ) : <span className="text-white/40">Free Agent</span>}
          </div>
          <div className="col-span-1 text-right font-mono-tech text-white/80">{row.wins}</div>
          <div className="col-span-1 text-right font-mono-tech text-white/80">{row.podiums}</div>
          <div className="col-span-2 text-right">
            <div className="font-mono-tech text-2xl text-rtrl-red">{row.points}</div>
            {i > 0 && <div className="small-label">-{leader - row.points}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function SyndicatesTable({ discipline, seasonId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { 
    const url = seasonId 
      ? `/standings/syndicates?discipline=${discipline}&season_id=${seasonId}`
      : `/standings/syndicates?discipline=${discipline}`;
    api.get(url).then((r) => setRows(r.data)); 
  }, [discipline, seasonId]);
  return (
    <div className="rtrl-card carbon-bg overflow-hidden">
      <div className="grid grid-cols-12 px-4 py-3 small-label border-b border-surface-line">
        <div className="col-span-1">Pos</div>
        <div className="col-span-6">Syndicate</div>
        <div className="col-span-2 text-right">Wins</div>
        <div className="col-span-3 text-right">Points</div>
      </div>
      {rows.map((row, i) => (
        <Link key={row.syndicate.id} to={`/syndicates/${row.syndicate.id}`} data-testid={`standings-syn-row-${i}`}
          className="grid grid-cols-12 items-center px-4 py-4 border-b border-surface-line/60 hover:bg-white/[0.02] cursor-pointer transition-colors"
          style={{ borderLeft: `4px solid ${row.syndicate.color}` }}>
          <div className={`col-span-1 font-display text-4xl ${i === 0 ? "text-white" : "text-rtrl-silver"}`}>{i + 1}</div>
          <div className="col-span-6 group flex items-center gap-3">
            {row.syndicate.logo_url ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${row.syndicate.logo_url}`} alt="" className="w-8 h-8 rounded-full object-contain bg-void/50 shrink-0" />
            ) : (
              <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: row.syndicate.color }} />
            )}
            <div>
              <div className="text-white font-body uppercase tracking-wider text-lg group-hover:text-rtrl-red transition-colors">{row.syndicate.name}</div>
              <div className="small-label">{row.syndicate.tag}</div>
            </div>
          </div>
          <div className="col-span-2 text-right font-mono-tech text-white/80">{row.wins}</div>
          <div className="col-span-3 text-right font-mono-tech text-3xl text-rtrl-red">{row.points}</div>
        </Link>
      ))}
    </div>
  );
}

export default function Standings() {
  const [activeRacerId, setActiveRacerId] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState("");

  useEffect(() => {
    api.get("/seasons")
      .then((res) => {
        setSeasons(res.data);
        const active = res.data.find(x => x.is_active);
        if (active) setSelectedSeasonId(active.id);
        else if (res.data.length > 0) setSelectedSeasonId(res.data[0].id);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="small-label text-rtrl-red">Season Standings · Live</div>
          <h1 className="font-display uppercase text-5xl md:text-6xl text-white mt-2">Standings</h1>
        </div>
        
        {seasons.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="small-label text-rtrl-silver font-semibold">Season:</span>
            <select
              className="rtrl-input w-48 text-sm"
              value={selectedSeasonId}
              onChange={(e) => setSelectedSeasonId(e.target.value)}
              data-testid="standings-season-select"
            >
              {seasons.map(s => (
                <option key={s.id} value={s.id}>{s.name} {s.is_active ? "(Active)" : ""}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <p className="text-white/60 mt-2">Split by discipline. Points auto-calculated from race results. Click on racers or syndicates to view details.</p>

      <Tabs defaultValue="drivers" className="mt-10">
        <TabsList className="bg-surface border border-surface-line hud-chamfer-sm" data-testid="standings-tabs">
          <TabsTrigger value="drivers" data-testid="standings-tab-drivers">Drivers</TabsTrigger>
          <TabsTrigger value="syndicates" data-testid="standings-tab-syndicates">Syndicate Cup</TabsTrigger>
        </TabsList>

        <TabsContent value="drivers">
          <Tabs defaultValue="pod" className="mt-6">
            <TabsList className="bg-transparent gap-2">
              <TabsTrigger value="pod" data-testid="drivers-pod" className="data-[state=active]:text-pod data-[state=active]:border-pod border-2 border-transparent px-4">Pod Racing</TabsTrigger>
              <TabsTrigger value="speeder" data-testid="drivers-speeder" className="data-[state=active]:text-speeder data-[state=active]:border-speeder border-2 border-transparent px-4">Speeder Racing</TabsTrigger>
            </TabsList>
            <TabsContent value="pod" className="mt-4"><DriversTable discipline="pod" onRacerClick={setActiveRacerId} seasonId={selectedSeasonId} /></TabsContent>
            <TabsContent value="speeder" className="mt-4"><DriversTable discipline="speeder" onRacerClick={setActiveRacerId} seasonId={selectedSeasonId} /></TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="syndicates">
          <Tabs defaultValue="pod" className="mt-6">
            <TabsList className="bg-transparent gap-2">
              <TabsTrigger value="pod" data-testid="syn-pod" className="data-[state=active]:text-pod data-[state=active]:border-pod border-2 border-transparent px-4">Pod Cup</TabsTrigger>
              <TabsTrigger value="speeder" data-testid="syn-speeder" className="data-[state=active]:text-speeder data-[state=active]:border-speeder border-2 border-transparent px-4">Speeder Cup</TabsTrigger>
            </TabsList>
            <TabsContent value="pod" className="mt-4"><SyndicatesTable discipline="pod" seasonId={selectedSeasonId} /></TabsContent>
            <TabsContent value="speeder" className="mt-4"><SyndicatesTable discipline="speeder" seasonId={selectedSeasonId} /></TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      <PublicRacerModal racerId={activeRacerId} onClose={() => setActiveRacerId(null)} />
    </div>
  );
}
